import {_} from 'lodash';
import {
    ICompanySyncstep,
    IdName,
    IExportAttribute,
    IExportTemplate,
    IFormTemplate,
    IImportHrFile,
    IMessage,
    IOrganization,
    IRuleset,
    ISyncField,
    ISyncstep,
    ISystem,
    ISystemRole
} from "../index";
import {SYSTEM_MEGA_EADM} from "../constants";
import {getObjectDefinition, getObjectDisplayName, getRawSyncFieldValue, getRoleName} from "./misc";
import {nsSyncFields} from "../req/nsSyncFields";
import {EMPTY_STRING} from "../constants/Language";
import {nsSync} from "./nsSync";

export namespace nsTooltip {

    const TEXT_NONE = '[((None))]';
    const TEXT_NA = '[((N/A))]';

    function yesno(obj: any): 'yes' | 'no' | typeof TEXT_NA {
        if (!_.isBoolean(obj)) {
            return TEXT_NA
        }
        return obj ? 'yes' : 'no'
    }

    export function getTitle_ruleset(o: Partial<IRuleset>): string | null {
        if (!o) return null
        let ret = `((ruleset|uc))

((name)):
${o.name}

((id)):
${o.id}

((object type)):
((${o.objecttype?.name || TEXT_NONE}))

((description)):
${o.description || TEXT_NONE}

((ruleset.departmentexpression.label)):
${o.departmentexpression || TEXT_NONE}

((ruleset.evaluatepositions.label)):
((${yesno(o.evaluatepositions)}))

((rule|p)):
${o.rules?.length ?? TEXT_NONE}`

        return ret
    }

    export function getTitle_system(o: ISystem): string | null {
        if (!o) return null
        let ret = `((collection|uc))

((name)):
${o.name}

((id)):
${o.id}

((description)):
${o.description || TEXT_NONE}

((multiple permissions)) ((per department|ac)):
((${yesno(o.multiplerolesamedepartment)}))

((multiple permissions)) ((per organization|ac))
((${yesno(o.multiplerolesamecompany)}))

((prioritize permissions))
((${yesno(o.prioritybasedroles)}))

((remove manually assigned department permissions))
((${yesno(o.purgemanualdepartmentpermissions)}))

((available for)):
${o.availableto_id || TEXT_NONE}

((system owner)):
${o.systemowner || TEXT_NONE}

((permission|p)):
`

        ret += o.roles?.length
            ? _(o.roles)
                .sortBy((r: ISystemRole) => (o.name == SYSTEM_MEGA_EADM) ? r.roleenum : r.name.toLowerCase())
                .map((r: ISystemRole) => getRoleName(o, r))
                .map((s: string) => `• ${s}`)
                .join('\n')
            : TEXT_NA

        return ret
    }

    export function getTitle_role(s: IdName | ISystem, r: ISystemRole): string | null {
        if (!s || !r) return null
        let ret = `((permission|uc))

((Name)):
${getRoleName(s, r)}

((id)):
${r.id}

((description)):
${r.description || TEXT_NONE}

((collection)):
${s.name}

((External ID)):
${r.roleenum}

((system owner)):
${r.systemowner || TEXT_NONE}

((metadata)):
${r.metadata || TEXT_NONE}
`

        return ret
    }

    export function getTitle_importHrFile(o: IImportHrFile): string | null {
        if (!o) return null
        let ret = `((synchronization cycle|uc))
        
((id)):
${o.id}

((started)): ${o.started}
((completed)): ${o.ended}

((account|p|uc)) (((error|ac)))
((new)): ${o.newaccounts} (${o.errornewaccounts})
((changed)): ${o.editedaccounts} (${o.erroreditedaccounts})
((deleted)): ${o.deletedaccounts} (${o.errordeletedaccounts})
((restored)): ${o.recoveredaccounts} (${o.errorrecoveredaccounts})

((group|p|uc)) (((error|ac)))
((new)): ${o.newgroups} (${o.errornewgroups})
((changed)): ${o.editedgroups} (${o.erroreditedgroups})
((deleted)): ${o.deletedgroups} (${o.errordeletedgroups})

((department|p|uc)) (((error|ac)))
((new)): ${o.newdepartments} (${o.errornewdepartments})
((changed)): ${o.editeddepartments} (${o.errorediteddepartments})
((deleted)): ${o.deleteddepartments} (${o.errordeleteddepartments})
`

        return ret
    }

    export function getTitle_exportAttribute(o: IExportAttribute): string | null {
        if (!o) return null
        let ret = `((exportattribute|uc))
        
((name)):
${o.name}

((type)):
${o.type}`

        if (o.type == 'Reference') {
            ret += `
            
Reference:
${JSON.stringify(o.referenceobjecttype)}`
        }
        return ret
    }

    export function getTitle_syncfield(o: ISyncField): string | null {
        if (!o) return null
        let ret = `((synchronization field|uc))
        
((name)):
${o.name}

((id)):
${o.syncfieldid}

((object type)):
((${getObjectDefinition(o.objecttypeid)?.translation ?? TEXT_NA}))

((translation)):
${o.displayname || TEXT_NONE}

((description)):
${o.description || TEXT_NONE}
`

        return ret
    }

    export function getTitle_objectdata(o: nsSyncFields.TObjectData_basic): string | null {
        if (!o) return null
        const def = getObjectDefinition(o.type)
        if (!def) return null
        let ret = `((${o.manual === true ? def.translationManual: def.translation}|uc))

((name)):
${getObjectDisplayName(o)}

((id)):
${o.id}`

        o.syncfields
            .filter(sf => {
                if (sf.value === undefined) {
                    return false
                }
                if (['Name', 'DisplayName', 'Id'].includes(sf.name)) {
                    // already shown
                    return false
                }
                if (sf.required || sf.core) {
                    return true
                }
                if (['Description', 'Department'].includes(sf.name)) {
                    return true
                }
                return false
            })
            .forEach(sf => {
                const val = getRawSyncFieldValue(sf.name, o.syncfields)
                const output = val === ''
                    ? EMPTY_STRING
                    : String(val)
                ;
                ret += `
                
((${sf.name})):
${output}`
            })
        return ret
    }

    export function getTitle_exportTemplate(o: IExportTemplate): string | null {
        if (!o) return null
        let ret = `((synchronization template|uc))

((name)):
${o.name}

((id)):
${o.id}

((active)):
((${yesno(o.active)}))

((object type)):
((${getObjectDefinition(o.objecttype)?.translation ?? TEXT_NA}))

((description)):
${o.description || TEXT_NONE}`

        return ret
    }

    export function getTitle_syncstep(o: ISyncstep): string {
        let ret = `((synchronization step|uc))

((name)):
${nsSync.getSyncstepName(o)}

((id)):
${o.id}

isexportable
((${yesno(o.isexportable)}))

supportsconfigkey
((${yesno(o.supportsconfigkey)}))

supportspasswordexport
((${yesno(o.supportspasswordexport)}))`

        return ret
    }

    export function getTitle_messageflow(o: IMessage): string {
        let ret = `((messageflow|uc))

((name)):
${o.name}

((id)):
${o.id}

((active)):
((${yesno(o.active)}))

((description)):
${o.description || TEXT_NONE}`

        return ret
    }

    export function getTitle_organization(o: IOrganization): string {
        let ret = `((organization|uc))

((name)):
${o.organizationame}

((id)):
${o.organizationid}

((import format)):
${o.importformat.name}

((delta synchronization)):
((${yesno(o.deltasync)}))

eHub:
((${yesno(o.ehub)}))`

        return ret
    }

    export function getTitle_formtemplate(o: IFormTemplate): string {
        let ret = `((synchronization step|uc))

((name)):
${o.name}

((id)):
${o.id}

((active)):
((${yesno(o.active)}))`

        return ret
    }

    export function getTitle_companysyncstep(o: ICompanySyncstep): string {
        let ret = `((synchronization step|uc))

((id)):
${o.syncstep.id}

((name)):
${nsSync.getSyncstepName(o.syncstep)}`

        if (o.description) {
            ret += `
                        
((description)):
${o.description}`
        }

        ret += `

((active)):
`

        if (o.active) {
            ret += `((yes))`
        } else {
            ret += `((no))`
        }

        ret += `
        
((domain)):
${o.domain}`

        const rulesets = o.ruleset
        if (rulesets?.length) {
            ret += `
                        
((ruleset)):
${rulesets.map(o => '• ' + o.name).join('\n')}`
        }

        const systemName = o.system?.name
        if (systemName) {
            ret += `
                       
((collection)):
${systemName}`
        }

        return ret

    }
}
